(function () {
  const SELECTOR = 'input[type="color"].hex-with-swatch__color';
  const READY_ATTR = 'data-koebi-color-picker-ready';
  const ACTIVE_ATTR = 'data-koebi-color-picker-active';
  const MOBILE_QUERY = '(hover: none) and (pointer: coarse)';
  const TAP_MOVE_THRESHOLD = 10;
  const TAP_SUPPRESS_MS = 450;
  const GUARDED_SURFACE_SELECTOR = '[data-koebi-label-editor], [data-koebi-label-configurator]';
  const GUARDED_INTERACTIVE_SELECTOR = [
    'input:not([type="hidden"])',
    'textarea',
    'select',
    'button',
    'label[for]',
    '[role="button"]',
    'a.button',
  ].join(',');

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const clamp01 = (value) => clamp(Number(value) || 0, 0, 1);
  const toHexPart = (value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');
  const rgbToHex = (r, g, b) => `#${toHexPart(r)}${toHexPart(g)}${toHexPart(b)}`;

  function normalizeHex(value) {
    let hex = String(value || '').trim().toLowerCase();
    if (!hex.startsWith('#')) hex = `#${hex}`;
    if (/^#[0-9a-f]{3}$/.test(hex)) {
      hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
    }
    return /^#[0-9a-f]{6}$/.test(hex) ? hex : '#ffffff';
  }

  function hexToRgb(hex) {
    const normalized = normalizeHex(hex).slice(1);
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
    };
  }

  function rgbToHsv(r, g, b) {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const delta = max - min;
    let h = 0;

    if (delta !== 0) {
      if (max === rn) h = ((gn - bn) / delta) % 6;
      else if (max === gn) h = (bn - rn) / delta + 2;
      else h = (rn - gn) / delta + 4;
      h *= 60;
      if (h < 0) h += 360;
    }

    return {
      h,
      s: max === 0 ? 0 : delta / max,
      v: max,
    };
  }

  function hsvToRgb(h, s, v) {
    const hue = ((Number(h) || 0) % 360 + 360) % 360;
    const sat = clamp01(s);
    const val = clamp01(v);
    const c = val * sat;
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = val - c;
    let r = 0;
    let g = 0;
    let b = 0;

    if (hue < 60) [r, g, b] = [c, x, 0];
    else if (hue < 120) [r, g, b] = [x, c, 0];
    else if (hue < 180) [r, g, b] = [0, c, x];
    else if (hue < 240) [r, g, b] = [0, x, c];
    else if (hue < 300) [r, g, b] = [x, 0, c];
    else [r, g, b] = [c, 0, x];

    return {
      r: Math.round((r + m) * 255),
      g: Math.round((g + m) * 255),
      b: Math.round((b + m) * 255),
    };
  }

  function shouldUseCustomPicker() {
    if (!window.matchMedia) return false;
    return window.matchMedia(MOBILE_QUERY).matches
      || (window.matchMedia('(max-width: 900px)').matches && window.matchMedia('(any-pointer: coarse)').matches);
  }

  function isVerticalScrollIntent(startX, startY, currentX, currentY) {
    const dx = Math.abs(currentX - startX);
    const dy = Math.abs(currentY - startY);
    return dy > TAP_MOVE_THRESHOLD && dy > dx * 1.1;
  }

  const picker = {
    root: null,
    activeInput: null,
    state: { h: 0, s: 0, v: 1 },
    closeTimer: null,
  };

  const colorTap = {
    pointerId: null,
    input: null,
    startX: 0,
    startY: 0,
    moved: false,
    suppressInput: null,
    ignoreClickUntil: 0,
  };

  const scrollGuard = {
    pointerId: null,
    target: null,
    startX: 0,
    startY: 0,
    snapshot: null,
    suppressUntil: 0,
    suppressTarget: null,
  };

  function buildPicker() {
    if (picker.root) return picker.root;

    const root = document.createElement('div');
    root.className = 'koebi-color-picker';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Farbe auswaehlen');
    root.hidden = true;
    root.innerHTML = `
      <div class="koebi-color-picker__sv" data-koebi-color-sv tabindex="0" aria-label="Saettigung und Helligkeit">
        <span class="koebi-color-picker__sv-marker" data-koebi-color-sv-marker></span>
      </div>
      <div class="koebi-color-picker__controls">
        <button type="button" class="koebi-color-picker__eyedropper" data-koebi-color-eyedropper aria-label="Farbe vom Bildschirm aufnehmen">
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M19.7 4.3a2.4 2.4 0 0 0-3.4 0l-2 2-.9-.9a1 1 0 1 0-1.4 1.4l.9.9-7.7 7.7a1 1 0 0 0-.3.7v2l-1.6 1.6a1 1 0 0 0 1.4 1.4l1.6-1.6h2a1 1 0 0 0 .7-.3l7.7-7.7.9.9a1 1 0 0 0 1.4-1.4l-.9-.9 2-2a2.4 2.4 0 0 0 0-3.4Zm-11.8 13.2h-1v-1l7.4-7.4 1 1-7.4 7.4Z" fill="currentColor"/>
          </svg>
        </button>
        <span class="koebi-color-picker__preview" data-koebi-color-preview aria-hidden="true"></span>
        <label class="koebi-color-picker__hue-label">
          <span class="visually-hidden">Farbton</span>
          <input class="koebi-color-picker__hue" data-koebi-color-hue type="range" min="0" max="360" step="1" value="0">
        </label>
      </div>
      <div class="koebi-color-picker__rgb" aria-label="RGB-Werte">
        <label class="koebi-color-picker__channel">
          <input data-koebi-color-channel="r" type="number" inputmode="numeric" min="0" max="255" step="1">
          <span>R</span>
        </label>
        <label class="koebi-color-picker__channel">
          <input data-koebi-color-channel="g" type="number" inputmode="numeric" min="0" max="255" step="1">
          <span>G</span>
        </label>
        <label class="koebi-color-picker__channel">
          <input data-koebi-color-channel="b" type="number" inputmode="numeric" min="0" max="255" step="1">
          <span>B</span>
        </label>
      </div>
    `;

    document.body.appendChild(root);
    picker.root = root;
    bindPicker(root);
    return root;
  }

  function getPickerParts() {
    const root = buildPicker();
    return {
      root,
      sv: root.querySelector('[data-koebi-color-sv]'),
      svMarker: root.querySelector('[data-koebi-color-sv-marker]'),
      hue: root.querySelector('[data-koebi-color-hue]'),
      preview: root.querySelector('[data-koebi-color-preview]'),
      channels: root.querySelectorAll('[data-koebi-color-channel]'),
      eyedropper: root.querySelector('[data-koebi-color-eyedropper]'),
    };
  }

  function renderPicker() {
    const { root, svMarker, hue, preview, channels } = getPickerParts();
    const rgb = hsvToRgb(picker.state.h, picker.state.s, picker.state.v);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);

    root.style.setProperty('--koebi-color-picker-hue', picker.state.h);
    root.style.setProperty('--koebi-color-picker-current', hex);
    if (svMarker) {
      svMarker.style.left = `${picker.state.s * 100}%`;
      svMarker.style.top = `${(1 - picker.state.v) * 100}%`;
    }
    if (hue) hue.value = Math.round(picker.state.h);
    if (preview) preview.style.background = hex;
    channels.forEach((input) => {
      const channel = input.dataset.koebiColorChannel;
      input.value = rgb[channel];
    });
  }

  function setStateFromHex(hex) {
    const rgb = hexToRgb(hex);
    picker.state = rgbToHsv(rgb.r, rgb.g, rgb.b);
    renderPicker();
  }

  function setInputColor(hex, commitChange) {
    const input = picker.activeInput;
    const normalized = normalizeHex(hex);
    if (!input) return;

    input.value = normalized;
    input.style.background = normalized;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    if (commitChange) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function applyPickerState(commitChange) {
    const rgb = hsvToRgb(picker.state.h, picker.state.s, picker.state.v);
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    renderPicker();
    setInputColor(hex, commitChange);
  }

  function updateFromSvEvent(event, commitChange) {
    const { sv } = getPickerParts();
    const rect = sv.getBoundingClientRect();
    picker.state.s = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    picker.state.v = 1 - clamp((event.clientY - rect.top) / rect.height, 0, 1);
    applyPickerState(commitChange);
  }

  function updatePosition(target) {
    const { root } = getPickerParts();
    const rect = target.getBoundingClientRect();
    const gap = 8;
    const width = Math.min(480, window.innerWidth - 24);

    root.style.width = `${width}px`;

    if (window.matchMedia('(max-width: 749px)').matches) {
      root.style.left = '50%';
      root.style.top = '50%';
      root.style.transform = 'translate(-50%, -50%)';
      return;
    }

    const left = clamp(rect.right - width, 12, window.innerWidth - width - 12);
    const below = rect.bottom + gap;
    const rootHeight = root.offsetHeight || 500;
    const top = below + rootHeight > window.innerHeight - 12
      ? clamp(rect.top - rootHeight - gap, 12, window.innerHeight - rootHeight - 12)
      : below;

    root.style.left = `${left}px`;
    root.style.top = `${top}px`;
    root.style.transform = 'none';
  }

  function openPicker(input) {
    if (!input || input.disabled) return;
    const { root, eyedropper } = getPickerParts();
    clearTimeout(picker.closeTimer);

    if (picker.activeInput && picker.activeInput !== input) {
      picker.activeInput.removeAttribute(ACTIVE_ATTR);
    }

    picker.activeInput = input;
    input.setAttribute(ACTIVE_ATTR, 'true');
    root.hidden = false;
    root.classList.add('is-open');
    if (eyedropper) {
      eyedropper.disabled = !('EyeDropper' in window);
    }

    setStateFromHex(input.value || input.style.background || '#ffffff');
    updatePosition(input);
  }

  function closePicker(commitChange) {
    if (!picker.root || picker.root.hidden) return;
    if (commitChange && picker.activeInput) {
      picker.activeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (picker.activeInput) {
      picker.activeInput.removeAttribute(ACTIVE_ATTR);
    }
    picker.root.classList.remove('is-open');
    picker.closeTimer = setTimeout(() => {
      if (picker.root) picker.root.hidden = true;
    }, 120);
    picker.activeInput = null;
  }

  function suppressColorClick(input) {
    colorTap.suppressInput = input;
    colorTap.ignoreClickUntil = Date.now() + TAP_SUPPRESS_MS;
  }

  function clearColorTap() {
    colorTap.pointerId = null;
    colorTap.input = null;
    colorTap.moved = false;
  }

  function isColorClickSuppressed(input) {
    return colorTap.suppressInput === input && Date.now() < colorTap.ignoreClickUntil;
  }

  function bindPicker(root) {
    const { sv, hue, channels, eyedropper } = getPickerParts();

    let svPointerId = null;
    sv.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      svPointerId = event.pointerId;
      sv.setPointerCapture(svPointerId);
      updateFromSvEvent(event, false);
    });
    sv.addEventListener('pointermove', (event) => {
      if (svPointerId !== event.pointerId) return;
      event.preventDefault();
      updateFromSvEvent(event, false);
    });
    ['pointerup', 'pointercancel'].forEach((eventName) => {
      sv.addEventListener(eventName, (event) => {
        if (svPointerId !== event.pointerId) return;
        svPointerId = null;
        updateFromSvEvent(event, true);
      });
    });
    sv.addEventListener('keydown', (event) => {
      const step = event.shiftKey ? 0.1 : 0.02;
      if (!['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'ArrowRight') picker.state.s = clamp01(picker.state.s + step);
      if (event.key === 'ArrowLeft') picker.state.s = clamp01(picker.state.s - step);
      if (event.key === 'ArrowUp') picker.state.v = clamp01(picker.state.v + step);
      if (event.key === 'ArrowDown') picker.state.v = clamp01(picker.state.v - step);
      applyPickerState(true);
    });

    hue.addEventListener('input', (event) => {
      picker.state.h = clamp(Number(event.target.value) || 0, 0, 360);
      applyPickerState(false);
    });
    hue.addEventListener('change', () => applyPickerState(true));

    channels.forEach((input) => {
      input.addEventListener('input', () => {
        const rgb = { r: 0, g: 0, b: 0 };
        channels.forEach((channelInput) => {
          const channel = channelInput.dataset.koebiColorChannel;
          rgb[channel] = clamp(parseInt(channelInput.value, 10) || 0, 0, 255);
        });
        picker.state = rgbToHsv(rgb.r, rgb.g, rgb.b);
        applyPickerState(false);
      });
      input.addEventListener('change', () => applyPickerState(true));
    });

    eyedropper.addEventListener('click', async () => {
      if (!('EyeDropper' in window)) return;
      try {
        const result = await new window.EyeDropper().open();
        if (result && result.sRGBHex) {
          setStateFromHex(result.sRGBHex);
          applyPickerState(true);
        }
      } catch (error) {
        // User cancelled the picker.
      }
    });

    root.addEventListener('pointerdown', (event) => event.stopPropagation());
    root.addEventListener('click', (event) => event.stopPropagation());
  }

  function bindInput(input) {
    if (!input || input.hasAttribute(READY_ATTR)) return;
    input.setAttribute(READY_ATTR, 'true');
    input.style.background = normalizeHex(input.value);
    input.setAttribute('aria-haspopup', 'dialog');

    input.addEventListener('input', () => {
      input.style.background = normalizeHex(input.value);
      if (picker.activeInput === input && picker.root && !picker.root.hidden) {
        setStateFromHex(input.value);
      }
    });

    input.addEventListener('pointerdown', (event) => {
      if (!shouldUseCustomPicker()) return;
      if (event.button != null && event.button !== 0) return;
      event.preventDefault();
      event.stopPropagation();
      colorTap.pointerId = event.pointerId;
      colorTap.input = input;
      colorTap.startX = event.clientX;
      colorTap.startY = event.clientY;
      colorTap.moved = false;
    }, true);

    input.addEventListener('pointermove', (event) => {
      if (colorTap.pointerId !== event.pointerId || colorTap.input !== input) return;
      if (isVerticalScrollIntent(colorTap.startX, colorTap.startY, event.clientX, event.clientY)) {
        colorTap.moved = true;
      }
    }, true);

    input.addEventListener('pointerup', (event) => {
      if (colorTap.pointerId !== event.pointerId || colorTap.input !== input) return;
      event.preventDefault();
      event.stopPropagation();
      if (colorTap.moved) {
        suppressColorClick(input);
        clearColorTap();
        return;
      }
      openPicker(input);
      suppressColorClick(input);
      clearColorTap();
    }, true);

    input.addEventListener('pointercancel', (event) => {
      if (colorTap.pointerId !== event.pointerId || colorTap.input !== input) return;
      suppressColorClick(input);
      clearColorTap();
    }, true);

    input.addEventListener('click', (event) => {
      if (!shouldUseCustomPicker()) return;
      event.preventDefault();
      event.stopPropagation();
      if (isColorClickSuppressed(input)) return;
      openPicker(input);
    }, true);

    input.addEventListener('keydown', (event) => {
      if (!shouldUseCustomPicker()) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openPicker(input);
    });
  }

  function initColorPickers(scope) {
    const root = scope || document;
    root.querySelectorAll(SELECTOR).forEach(bindInput);
  }

  function getGuardedInteractive(target) {
    if (!shouldUseCustomPicker() || !target || !target.closest) return null;
    if (target.closest('.koebi-color-picker')) return null;
    const interactive = target.closest(GUARDED_INTERACTIVE_SELECTOR);
    if (!interactive || !interactive.closest(GUARDED_SURFACE_SELECTOR)) return null;
    return interactive;
  }

  function snapshotInteractive(el) {
    if (!el || !('tagName' in el)) return null;
    const tagName = el.tagName.toLowerCase();
    if (tagName === 'input') {
      return {
        value: el.value,
        checked: el.checked,
        selectedIndex: el.selectedIndex,
      };
    }
    if (tagName === 'textarea' || tagName === 'select') {
      return {
        value: el.value,
        selectedIndex: el.selectedIndex,
      };
    }
    return null;
  }

  function restoreInteractive(el, snapshot) {
    if (!el || !snapshot) return;
    let changed = false;
    if ('value' in snapshot && el.value !== snapshot.value) {
      el.value = snapshot.value;
      changed = true;
    }
    if ('checked' in snapshot && el.checked !== snapshot.checked) {
      el.checked = snapshot.checked;
      changed = true;
    }
    if ('selectedIndex' in snapshot && el.selectedIndex !== snapshot.selectedIndex) {
      el.selectedIndex = snapshot.selectedIndex;
      changed = true;
    }
    if (changed) {
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function suppressGuardedTarget(target) {
    if (!target) return;
    restoreInteractive(target, scrollGuard.snapshot);
    scrollGuard.suppressTarget = target;
    scrollGuard.suppressUntil = Date.now() + TAP_SUPPRESS_MS;
  }

  function clearScrollGuard() {
    scrollGuard.pointerId = null;
    scrollGuard.target = null;
    scrollGuard.snapshot = null;
  }

  function isGuardSuppressed(target) {
    const suppressed = scrollGuard.suppressTarget;
    return !!suppressed
      && Date.now() < scrollGuard.suppressUntil
      && (target === suppressed || (target.closest && target.closest(GUARDED_INTERACTIVE_SELECTOR) === suppressed));
  }

  function bindMobileScrollGuard() {
    document.addEventListener('pointerdown', (event) => {
      const target = getGuardedInteractive(event.target);
      if (!target) return;
      scrollGuard.pointerId = event.pointerId;
      scrollGuard.target = target;
      scrollGuard.startX = event.clientX;
      scrollGuard.startY = event.clientY;
      scrollGuard.snapshot = snapshotInteractive(target);
    }, true);

    document.addEventListener('pointermove', (event) => {
      if (scrollGuard.pointerId !== event.pointerId || !scrollGuard.target) return;
      if (!isVerticalScrollIntent(scrollGuard.startX, scrollGuard.startY, event.clientX, event.clientY)) return;
      suppressGuardedTarget(scrollGuard.target);
    }, true);

    document.addEventListener('pointerup', (event) => {
      if (scrollGuard.pointerId !== event.pointerId || !scrollGuard.target) return;
      if (isGuardSuppressed(scrollGuard.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
      clearScrollGuard();
    }, true);

    document.addEventListener('pointercancel', (event) => {
      if (scrollGuard.pointerId !== event.pointerId || !scrollGuard.target) return;
      suppressGuardedTarget(scrollGuard.target);
      clearScrollGuard();
    }, true);

    document.addEventListener('click', (event) => {
      if (!isGuardSuppressed(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    document.addEventListener('focusin', (event) => {
      if (!isGuardSuppressed(event.target)) return;
      event.preventDefault();
      if (typeof event.target.blur === 'function') event.target.blur();
    }, true);
  }

  document.addEventListener('pointerdown', () => closePicker(true));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePicker(true);
  });
  window.addEventListener('resize', () => {
    if (picker.activeInput && picker.root && !picker.root.hidden) {
      updatePosition(picker.activeInput);
    }
  }, { passive: true });
  window.addEventListener('scroll', () => {
    if (picker.activeInput && picker.root && !picker.root.hidden) {
      updatePosition(picker.activeInput);
    }
  }, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initColorPickers(document));
  } else {
    initColorPickers(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initColorPickers(event.target);
  });

  bindMobileScrollGuard();
})();
